import React, { useMemo } from 'react';
import { Box, ThemeProvider, createTheme, useTheme } from '@mui/material';

export function portalPalette(pathname) {
  if (pathname.startsWith('/truck/') || pathname.startsWith('/trucks')) return { accent: '#3f7088', tint: '#dbeef5' };
  if (pathname.startsWith('/parts')) return { accent: '#775e8b', tint: '#ecdef4' };
  if (pathname.startsWith('/inventory')) return { accent: '#356d59', tint: '#d9eedf' };
  if (pathname.startsWith('/movements')) return { accent: '#866523', tint: '#f8e9c4' };
  if (pathname.startsWith('/maintenance-reminders')) return { accent: '#926339', tint: '#f8e6ce' };
  if (pathname.startsWith('/service-logs')) return { accent: '#655b8c', tint: '#e5def5' };
  if (pathname.startsWith('/mechanic-defects') || pathname.startsWith('/resolved-defects')) return { accent: '#356d59', tint: '#d9eee3' };
  if (pathname.startsWith('/drivers')) return { accent: '#87516d', tint: '#f3dfeb' };
  if (pathname.startsWith('/truck-scheduling')) return { accent: '#546f46', tint: '#e3edcf' };
  if (pathname.startsWith('/live-gps')) return { accent: '#367372', tint: '#d7eeea' };
  if (pathname.startsWith('/users')) return { accent: '#3b658b', tint: '#dceaf8' };
  if (pathname.startsWith('/profile')) return { accent: '#6d5b8c', tint: '#e5def5' };
  return { accent: '#367372', tint: '#d7eeea' };
}

export default function PortalPageTheme({ pathname, children }) {
  const outerTheme = useTheme();
  const { accent, tint } = portalPalette(pathname);
  const theme = useMemo(() => createTheme(outerTheme, {
    palette: { primary: { main: accent } },
    components: {
      MuiPaper: { styleOverrides: { root: { borderRadius: 12, boxShadow: '0 4px 18px rgba(16,42,67,.05)' } } },
      MuiButton: { styleOverrides: { root: { borderRadius: 8, fontWeight: 800, textTransform: 'none' } } },
      MuiDialog: { styleOverrides: { paper: { border: `1px solid ${accent}30`, borderTop: `3px solid ${accent}90` } } },
    },
  }), [outerTheme, accent]);

  if (pathname === '/dashboard') return children;

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: '100%', minWidth: 0,
        '& > .MuiBox-root': { width: '100%', maxWidth: 'none', boxSizing: 'border-box' },
        '& > .MuiBox-root > .MuiPaper-root': {
          borderRadius: 3,
          boxShadow: '0 4px 18px rgba(16,42,67,.05)',
        },
        '& .portal-page-header': {
          background: `linear-gradient(135deg, ${tint}, #ffffff)`, color: accent,
          borderTop: `3px solid ${accent}90`, borderRadius: 3,
          boxShadow: '0 4px 18px rgba(16,42,67,.05)',
          '& > .MuiBox-root:first-child': { background: 'transparent', color: accent },
          '& .MuiCardContent-root': { p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } },
          '& .MuiTypography-root': { color: accent },
          '& .MuiTypography-h3, & .MuiTypography-h4, & .MuiTypography-h5': { fontSize: { xs: '1.05rem', sm: '1.3rem' }, fontWeight: 900, lineHeight: 1.2 },
          '& .MuiAvatar-root': { background: `${accent}18`, color: accent, boxShadow: 'none', borderRadius: 2 },
          '& .MuiChip-root': { bgcolor: `${accent}12`, color: accent },
          '& .MuiButton-outlined': { color: accent, borderColor: `${accent}60` },
          '& .MuiButton-containedPrimary': { background: accent, color: 'white', '&:hover': { filter: 'brightness(.92)' }, '&:disabled': { background: '#e6eaf0', color: '#8391a2', filter: 'none' } },
        },
        '@media print': { '& .portal-page-header': { background: 'white', boxShadow: 'none' } },
      }}>{children}</Box>
    </ThemeProvider>
  );
}
